(function (FragranceFinder) {
  'use strict';

  const EMAIL_RE = FragranceFinder.EMAIL_RE;
  const mobileMediaQuery = FragranceFinder.mobileMediaQuery;
  const parseJson = FragranceFinder.parseJson;
  const escapeHtml = FragranceFinder.escapeHtml;
  const normalizeRichText = FragranceFinder.normalizeRichText;
  const fillProfileIntro = FragranceFinder.fillProfileIntro;
  const buildNoteSteps = FragranceFinder.buildNoteSteps;
  const getTranslations = FragranceFinder.getTranslations;
  const getSettingText = FragranceFinder.getSettingText;
  const Store = FragranceFinder.Store;
  const klaviyoTrack = FragranceFinder.klaviyoTrack;
  const prefersReducedMotion = FragranceFinder.prefersReducedMotion;
  const normalizeRedirectUrl = FragranceFinder.normalizeRedirectUrl;
  const localeUrl = FragranceFinder.localeUrl;
  const cssEscape = FragranceFinder.cssEscape;

  function ResultsController(root) {
  this.root = root;
  this.config = parseJson(root.querySelector('[data-ff-config]'), {});
  this.settings = this.config.settings || {};
  this.catalog = this.config.catalog || {};
  this.store = new Store(this.settings.persist !== false);

  this.content = root.querySelector('[data-ff-results-content]');
  this.fallback = root.querySelector('[data-ff-results-fallback]');

  this.syncViewportHeight();
  let controller = this;
  window.addEventListener('load', function () { controller.syncViewportHeight(); });
  window.addEventListener('resize', function () { controller.syncViewportHeight(); });
  window.addEventListener('orientationchange', function () { controller.syncViewportHeight(); });
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', function () { controller.syncViewportHeight(); });
  }

  // Read only — the quiz clears the store on load, so a restart already begins a
  // fresh run without this page dropping anything. Clearing here as well would
  // lose the results on a refresh, or on a back-navigation from a product page.
  let state = this.store.read();
  this.state = state;

  let result = state && state.result;
  let answers = (state && state.answers) || {};
  const valid = result && result.primaryId && this.catalog[result.primaryId];

  if (!valid) {
    this.showFallback();
  } else {
    this.render(result, answers);
  }

  this.bindRestart();
  this.bindEmail();
  this.bindQuickBuy();

  const onViewportChange = function () {
    if (controller._profileProduct) controller.applyProfileImage(controller._profileProduct);
  };
  if (mobileMediaQuery.addEventListener) mobileMediaQuery.addEventListener('change', onViewportChange);
  else if (mobileMediaQuery.addListener) mobileMediaQuery.addListener(onViewportChange);
}

ResultsController.prototype.syncViewportHeight = function () {
  const visualViewport = window.visualViewport;
  const height = (visualViewport && visualViewport.height) || window.innerHeight;
  if (height) this.root.style.setProperty('--ff-vh', height + 'px');
};

ResultsController.prototype.showFallback = function () {
  if (this.content) this.content.hidden = true;
  if (this.fallback) this.fallback.hidden = false;
};

ResultsController.prototype.render = function (result, answers) {
  if (this.fallback) this.fallback.hidden = true;
  if (this.content) this.content.hidden = false;

  let primary = this.catalog[result.primaryId];
  this.renderProfile(primary, answers);
  this.renderMatches(primary, result.recommendationIds || []);
};

function mobileProductImage(product) {
  return product.resultImageMobile || product.resultImage;
}

function collapseWhitespace(value) {
  return String(value == null ? '' : value).replace(/\s+/g, ' ').trim();
}

ResultsController.prototype.applyProfileImage = function (product) {
  let mediaElement = this.root.querySelector('[data-ff-profile-image]');
  if (!mediaElement) return;
  let source = mobileMediaQuery.matches ? mobileProductImage(product) : product.resultImage;
  if (source) { mediaElement.src = source; mediaElement.alt = product.title || ''; }
  else { const mediaWrapper = mediaElement.closest('.ff-profile__media'); if (mediaWrapper) mediaWrapper.hidden = true; }
};

ResultsController.prototype.renderProfile = function (product, answers) {
  let root = this.root;
  const setElementText = function (selector, value, isHtml) {
    const element = root.querySelector(selector);
    if (!element) return;
    if (value == null || value === '') { element.hidden = true; return; }
    element.hidden = false;
    if (isHtml) element.innerHTML = value; else element.textContent = value;
  };
  let introText = fillProfileIntro(this.settings.profileIntroTemplate, product, answers);
  let profileCopyHtml = normalizeRichText(product.scentProfileCopy || this.settings.profileFallbackCopy);
  // The scent profile metafield is the curated copy and wins. The product
  // description only stands in when that metafield is empty — most products now
  // hold the same sentence in both, which read as the paragraph saying it twice.
  let descriptionText = profileCopyHtml ? '' : collapseWhitespace(product.description);
  // Intro sentence and the product description read as one paragraph, so they
  // share the opening <p> of the scent profile copy.
  let leadText = [introText, descriptionText].filter(Boolean).join(' ');
  let combinedCopyHtml = profileCopyHtml;
  if (leadText) {
    let leadHtml = escapeHtml(leadText) + ' ';
    combinedCopyHtml = /^\s*<p[^>]*>/i.test(profileCopyHtml)
      ? profileCopyHtml.replace(/^(\s*<p[^>]*>)/i, '$1' + leadHtml)
      : leadHtml + profileCopyHtml;
  }
  setElementText('[data-ff-profile-copy]', combinedCopyHtml, true);

  this._profileProduct = product;
  this.applyProfileImage(product);

  const setRowText = function (selector, value) {
    const element = root.querySelector(selector);
    if (!element) return;
    const row = element.closest('.ff-profile__row') || element;
    const text = collapseWhitespace(value);
    element.textContent = text;
    row.hidden = !text;
    row.classList.remove('ff-profile__row--lead');
  };
  setRowText('[data-ff-profile-notes]', product.notesSummary);
  setRowText('[data-ff-profile-pref1]', product.preferenceCopy1);
  setRowText('[data-ff-profile-pref2]', product.preferenceCopy2);

  // The top rule belongs to the first row left showing, and the whole list goes
  // when this product has none of these fields set.
  const rowsWrapper = root.querySelector('.ff-profile__rows');
  if (rowsWrapper) {
    const leadRow = rowsWrapper.querySelector('.ff-profile__row:not([hidden])');
    if (leadRow) leadRow.classList.add('ff-profile__row--lead');
    rowsWrapper.hidden = !leadRow;
  }

  this.renderAddToCart(product);
  this.setupProfileHover(product);
  this.setupProfileMobile(product);
};

// The recommended product is only known here, client-side, so the section ships a
// <template> per catalog product and this picks the matching one. Cloning keeps
// the template intact; inserting it upgrades <product-form>/<quick-order-select>,
// so the block behaves exactly as it does on a collection card.
ResultsController.prototype.renderAddToCart = function (product) {
  const host = this.root.querySelector('[data-ff-profile-atc]');
  if (!host) return;
  const template = this.root.querySelector('template[data-ff-atc="' + cssEscape(String(product.id)) + '"]');
  host.innerHTML = '';
  host.hidden = !template;
  if (template) host.appendChild(template.content.cloneNode(true));
};

ResultsController.prototype.setupProfileHover = function (product) {
  if (mobileMediaQuery.matches) return;
  let root = this.root;
  let mediaElement = root.querySelector('[data-ff-profile-media]');
  if (!mediaElement) return;
  // Only focusable on desktop, where the listeners below actually reveal the
  // note pyramid — mobile has its own dedicated "more" button for this.
  mediaElement.setAttribute('tabindex', '0');

  const nameElement = root.querySelector('[data-ff-profile-name]');
  const hoverImageElement = root.querySelector('[data-ff-profile-image-hover]');
  const labelElement = root.querySelector('[data-ff-note-label]');
  const valuesElement = root.querySelector('[data-ff-note-values]');

  if (nameElement) nameElement.textContent = product.title || '';

  if (hoverImageElement) hoverImageElement.remove();

  let noteStepList = buildNoteSteps(product, getTranslations(this.settings));
  const hasPyramid = true;
  mediaElement.classList.toggle('has-hover', hasPyramid || !!product.title);

  let activeIndex = 0;
  let rotationTimer = null;
  function paint(index) {
    if (!noteStepList.length || !labelElement || !valuesElement) return;
    labelElement.textContent = noteStepList[index].label;
    valuesElement.textContent = noteStepList[index].values;
  }
  function start() {
    mediaElement.classList.add('is-hover');
    if (!hasPyramid) return;
    activeIndex = 0;
    paint(0);
    if (prefersReducedMotion() || noteStepList.length < 2) return;
    clearInterval(rotationTimer);
    rotationTimer = setInterval(function () {
      activeIndex = (activeIndex + 1) % noteStepList.length;
      paint(activeIndex);
    }, 1600);
  }
  function stop() {
    mediaElement.classList.remove('is-hover');
    clearInterval(rotationTimer);
    rotationTimer = null;
  }

  if (hasPyramid) paint(0);
  mediaElement.addEventListener('mouseenter', start);
  mediaElement.addEventListener('mouseleave', stop);
  mediaElement.addEventListener('focusin', start);
  mediaElement.addEventListener('focusout', stop);
};

ResultsController.prototype.setupProfileMobile = function (product) {
  if (!mobileMediaQuery.matches) return;
  let root = this.root;
  const mediaElement = root.querySelector('[data-ff-profile-media]');
  if (mediaElement) mediaElement.removeAttribute('tabindex');
  const drawer = root.querySelector('[data-ff-profile-drawer]');
  const track = root.querySelector('[data-ff-profile-track]');
  const moreButton = root.querySelector('[data-ff-profile-more]');
  const nameDetail = root.querySelector('[data-ff-profile-name-detail]');
  const pyramidListElement = root.querySelector('[data-ff-profile-pyramid-list]');
  const detailPanelElement = root.querySelector('.ff-profile__panel--detail');
  if (!drawer || !track) return;

  if (nameDetail) nameDetail.textContent = product.title || '';

  // Notes sit beside the same bottle — never paint a second bottle behind the text.
  if (detailPanelElement) detailPanelElement.style.backgroundImage = '';

  let noteStepList = buildNoteSteps(product, getTranslations(this.settings));

  if (pyramidListElement && noteStepList.length) {
    pyramidListElement.innerHTML = noteStepList.map(function (step) {
      return '<div class="ff-profile__pyramid-item">' +
        '<p class="ff-profile__pyramid-label">' + escapeHtml(step.label) + '</p>' +
        '<p class="ff-profile__pyramid-values">' + escapeHtml(step.values) + '</p>' +
        '</div>';
    }).join('');
  }

  if (moreButton) {
    moreButton.hidden = !(noteStepList.length || product.title);
    moreButton.addEventListener('click', function () { setDrawerOpen(true); });
  }

  // Track is 150% of the drawer; open shift is 50% of drawer = 1/3 of track.
  const OPEN_TRACK_PERCENT = -100 / 3;
  let isDrawerOpen = false;
  let isDragging = false;
  let startX = 0;
  let currentTranslatePercent = 0;

  function setDrawerOpen(open, animate) {
    isDrawerOpen = open;
    drawer.classList.toggle('is-open', open);
    if (animate === false) track.style.transition = 'none';
    currentTranslatePercent = open ? OPEN_TRACK_PERCENT : 0;
    // Let CSS own the settled open/closed transform; clear any drag override.
    track.style.transform = '';
    if (animate === false) { void track.offsetWidth; track.style.transition = ''; }
    if (detailPanelElement) detailPanelElement.setAttribute('aria-hidden', open ? 'false' : 'true');
  }

  drawer.addEventListener('pointerdown', function (event) {
    if (!noteStepList.length && !product.title) return;
    isDragging = true;
    startX = event.clientX;
    track.style.transition = 'none';
    try { drawer.setPointerCapture(event.pointerId); } catch (error) {}
  });
  drawer.addEventListener('pointermove', function (event) {
    if (!isDragging) return;
    const deltaX = event.clientX - startX;
    const drawerWidth = drawer.clientWidth || 1;
    // Convert drawer-width drag into track % (track = 1.5 × drawer).
    const startPercent = isDrawerOpen ? OPEN_TRACK_PERCENT : 0;
    let percent = startPercent + (deltaX / drawerWidth) * (100 / 1.5);
    percent = Math.max(OPEN_TRACK_PERCENT, Math.min(0, percent));
    currentTranslatePercent = percent;
    track.style.transform = 'translateX(' + percent + '%)';
  });
  function endDrag() {
    if (!isDragging) return;
    isDragging = false;
    track.style.transition = '';
    setDrawerOpen(currentTranslatePercent <= OPEN_TRACK_PERCENT / 2, true);
  }
  drawer.addEventListener('pointerup', endDrag);
  drawer.addEventListener('pointercancel', endDrag);
};

ResultsController.prototype.renderMatches = function (primary, recommendationIds) {
  const grid = this.root.querySelector('[data-ff-matches-grid]');
  const cardPool = this.root.querySelector('[data-ff-card-pool]');
  const dots = this.root.querySelector('[data-ff-dots]');
  if (!grid || !cardPool) return;

  const badges = this.settings.matchBadges || {};
  const translations = getTranslations(this.settings);
  const matchSlots = [
    { id: primary.id, badge: badges.best || translations.badgeBest },
    { id: recommendationIds[0], badge: badges.also || translations.badgeAlso },
    { id: recommendationIds[1], badge: badges.surprise || translations.badgeSurprise }
  ];

  grid.innerHTML = '';
  let addedCount = 0;
  matchSlots.forEach(function (slot) {
    if (!slot.id) return;
    let source = cardPool.querySelector('[data-ff-card="' + cssEscape(String(slot.id)) + '"]');
    if (!source) return;
    let card = source.cloneNode(true);
    card.hidden = false;
    const badgeElement = card.querySelector('[data-ff-card-badge]');
    if (badgeElement) badgeElement.textContent = slot.badge;
    grid.appendChild(card);
    addedCount++;
  });

  if (!addedCount) {
    const section = this.root.querySelector('[data-ff-matches]');
    if (section) section.hidden = true;
  } else {
    grid.setAttribute('data-ff-match-count', String(addedCount));
  }
  this.setupDots(grid, dots);
};

ResultsController.prototype.setupDots = function (grid, dots) {
  if (!dots) return;
  const cards = grid.children;
  if (cards.length < 2) { dots.hidden = true; return; }
  dots.hidden = false;
  dots.innerHTML = '';
  for (let index = 0; index < cards.length; index++) {
    (function (cardIndex) {
      const button = document.createElement('button');
      button.type = 'button';
      const dotTemplate = getTranslations(this.settings).matchDot || '';
      button.setAttribute('aria-label', dotTemplate.replace(/\{\{\s*number\s*\}\}/g, String(cardIndex + 1)));
      if (cardIndex === 0) button.setAttribute('aria-current', 'true');
      button.addEventListener('click', function () {
        cards[cardIndex].scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', inline: 'start', block: 'nearest' });
      });
      dots.appendChild(button);
    }).call(this, index);
  }
  grid.addEventListener('scroll', function () {
    const scrollAnchor = grid.scrollLeft + 16;
    let nearestIndex = 0;
    let minimumDistance = Infinity;
    for (let cardIndex = 0; cardIndex < cards.length; cardIndex++) {
      let card = cards[cardIndex];
      const distance = Math.abs(card.offsetLeft - scrollAnchor);
      if (distance < minimumDistance) { minimumDistance = distance; nearestIndex = cardIndex; }
    }
    for (let dotIndex = 0; dotIndex < dots.children.length; dotIndex++) {
      if (dotIndex === nearestIndex) dots.children[dotIndex].setAttribute('aria-current', 'true');
      else dots.children[dotIndex].removeAttribute('aria-current');
    }
  }, { passive: true });
};

ResultsController.prototype.bindRestart = function () {
  let controller = this;
  const buttons = this.root.querySelectorAll('[data-ff-restart]');
  Array.prototype.forEach.call(buttons, function (button) {
    button.addEventListener('click', function (event) {
      event.preventDefault();
      controller.store.clear();
      window.location.assign(
        normalizeRedirectUrl(controller.settings.quizUrl, localeUrl(controller.settings, '/'))
      );
    });
  });
};

ResultsController.prototype.bindEmail = function () {
  let controller = this;
  const form = this.root.querySelector('[data-ff-results-email]');
  if (!form) return;
  const input = form.querySelector('input');
  const message = form.querySelector('[data-ff-results-email-msg]');
  form.addEventListener('submit', function (event) {
    event.preventDefault();
    let value = input ? input.value.trim() : '';
    if (!EMAIL_RE.test(value)) {
      if (message) message.textContent = getSettingText(controller.settings, 'emailErrorInvalid', 'emailErrorInvalid');
      return;
    }
    let state = controller.state;
    let result = (state && state.result) || {};
    let primary = controller.catalog[result.primaryId] || {};
    klaviyoTrack(controller.settings.klaviyo, controller.settings.klaviyo && controller.settings.klaviyo.eventName, {
      $email: value,
      recommended_product_title: primary.title || '',
      recommended_product_handle: primary.handle || '',
      recommended_product_id: result.primaryId || '',
      source: 'results_page',
      locale: controller.settings.locale || '',
      language_name: controller.settings.languageName || '',
      country: controller.settings.country || '',
      currency: controller.settings.currency || '',
      timestamp: new Date().toISOString()
    });
    if (message) message.textContent = getSettingText(controller.settings, 'emailSuccess', 'emailSuccess');
    if (input) input.value = '';
  });
};


// The bag button on a match card is a one-click add. Which variant it adds is
// decided in Liquid (125ml for MEN, otherwise the product's default), so all
// this has to do is post it and hand the response to the theme's cart.
//
// It mirrors product-form.js rather than reusing <product-form>, which needs a
// real <form>: match cards are cloned out of the pool, and a form per card would
// put duplicate form ids in the document — the same reason the scent profile's
// add-to-cart block ships inside a <template>.
ResultsController.prototype.bindQuickBuy = function () {
  const controller = this;
  this.root.addEventListener('click', function (event) {
    const target = event.target;
    const button = target && target.closest ? target.closest('[data-ff-quick-buy]') : null;
    if (!button) return;
    event.preventDefault();
    controller.quickBuy(button);
  });
};

ResultsController.prototype.quickBuy = function (button) {
  const variantId = button.getAttribute('data-variant-id');
  if (!variantId) return;
  // Guards the double-click; cleared in the finally below.
  if (button.getAttribute('aria-disabled') === 'true') return;

  const routes = window.routes || {};
  const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  const config =
    typeof fetchConfig === 'function'
      ? fetchConfig('javascript')
      : { method: 'POST', headers: { Accept: 'application/javascript' } };
  config.headers['X-Requested-With'] = 'XMLHttpRequest';
  delete config.headers['Content-Type'];

  const formData = new FormData();
  formData.append('id', variantId);
  formData.append('quantity', '1');
  if (cart) {
    formData.append('sections', cart.getSectionsToRender().map(function (section) { return section.section; }));
    formData.append('sections_url', window.location.pathname);
    cart.setActiveElement(document.activeElement);
  }
  config.body = formData;

  const canPublish = typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined';

  button.setAttribute('aria-disabled', 'true');

  fetch(routes.cart_add_url || '/cart/add', config)
    .then(function (response) { return response.json(); })
    .then(function (response) {
      // A non-empty status means the add was rejected — sold out between render
      // and click, or a cart rule refused it. The drawer stays shut either way.
      if (response.status) {
        if (canPublish) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'fragrance-finder-results',
            productVariantId: variantId,
            errors: response.errors || response.description,
            message: response.message
          });
        }
        return;
      }

      if (canPublish) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'fragrance-finder-results',
          productVariantId: variantId,
          cartData: response
        });
      }

      if (!cart) {
        window.location = routes.cart_url || '/cart';
        return;
      }
      if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
      cart.renderContents(response);
    })
    .catch(function (error) { console.error(error); })
    .finally(function () {
      button.removeAttribute('aria-disabled');
    });
};

  FragranceFinder.ResultsController = ResultsController;
})(window.FragranceFinder = window.FragranceFinder || {});
